<template>
  <div
    v-if="isVisible"
    ref="overlay"
    class="movable-overlay"
    :style="overlayStyle"
    @mousedown.stop
  >
    <div class="overlay-header" @mousedown.stop.prevent="startDrag">
      <span>{{ overlayTitle }}</span>
      <button class="close-button" @click="closeOverlay">×</button>
    </div>

    <div class="overlay-content duallo-scroll">
      <slot @OnRefresh="handleRefresh"></slot>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    overlayTitle: {
      type: String,
      default: "Window",
    },
    positionX: {
      type: Number,
      default: 450,
    },
    positionY: {
      type: Number,
      default: 450,
    },
    centralizeWindow: {
      type: Boolean,
      default: false,
    },
    positionOffset: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      isDragging: false,
      startX: 0,
      startY: 0,
      localPositionX: this.positionX,
      localPositionY: this.positionY,
      // When true, the window is centred with a CSS transform (viewport-relative, so it stays put and
      // fits at any browser zoom). It flips to pixel positioning the moment the user drags it.
      centered: false,
      resizeObserver: null
    };
  },

  computed: {
    overlayStyle() {
      if (this.centered) {
        return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
      }
      return {
        top: `${this.localPositionY}px`,
        left: `${this.localPositionX}px`,
        transform: "none",
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.updatePosition();
      this.setupResizeObserver();
    });
  },

  beforeUnmount() {
    this.stopDrag();
    this.teardownResizeObserver();
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updatePosition();
          this.setupResizeObserver();
        });
      } else {
        this.teardownResizeObserver();
      }
    },

    centralizeWindow() {
      if (this.isVisible) {
        this.$nextTick(() => {
          this.updatePosition();
        });
      }
    },

    positionOffset() {
      if (this.isVisible) {
        this.$nextTick(() => {
          this.updatePosition();
        });
      }
    },

    positionX(newVal) {
      if (!this.centralizeWindow) {
        this.localPositionX = newVal;
      }
    },

    positionY(newVal) {
      if (!this.centralizeWindow) {
        this.localPositionY = newVal;
      }
    }
  },

  methods: {
    closeOverlay() {
      this.$emit("closeOverlay");
    },

    handleRefresh() {
      this.$emit("RefreshRequired");
      this.$nextTick(() => {
        this.updatePosition();
      });
    },

    updatePosition() {
      // Centralised windows centre via a pure CSS transform (see overlayStyle) — this holds at any
      // browser zoom and needs no viewport math. Non-centralised (movable) windows use pixel coords.
      if (this.centralizeWindow) {
        this.centered = true;
        return;
      }
      this.centered = false;
      this.localPositionX = this.positionX;
      this.localPositionY = this.positionY;
    },

    setupResizeObserver() {
      this.teardownResizeObserver();

      const overlay = this.$refs.overlay;
      if (!overlay || typeof ResizeObserver === "undefined") return;

      this.resizeObserver = new ResizeObserver(() => {
        if (this.centralizeWindow && !this.isDragging) {
          this.updatePosition();
        }
      });

      this.resizeObserver.observe(overlay);
    },

    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },

    startDrag(event) {
      // Hand off from CSS centring to pixel positioning at the window's current on-screen spot, so
      // the drag continues smoothly from where it sits.
      if (this.centered) {
        const rect = this.$refs.overlay?.getBoundingClientRect();
        if (rect) {
          this.localPositionX = rect.left;
          this.localPositionY = rect.top;
        }
        this.centered = false;
      }

      this.isDragging = true;
      this.startX = event.clientX - this.localPositionX;
      this.startY = event.clientY - this.localPositionY;

      document.addEventListener("mousemove", this.drag);
      document.addEventListener("mouseup", this.stopDrag);
    },

    drag(event) {
      if (!this.isDragging) return;

      const newX = event.clientX - this.startX;
      const newY = event.clientY - this.startY;

      const minY = 100;
      const maxY = window.innerHeight - 200;
      const minX = -600;
      const maxX = window.innerWidth - 300;

      this.localPositionX = Math.min(Math.max(newX, minX), maxX);
      this.localPositionY = Math.min(Math.max(newY, minY), maxY);
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.drag);
      document.removeEventListener("mouseup", this.stopDrag);
    },
  }
};
</script>

<style scoped>
.movable-overlay {
  position: fixed;
  min-width: min(800px, 95vw);
  max-width: 95vw;
  /* dvh keeps the window inside the visible viewport at any browser zoom; the header stays put and
     the content scrolls, so nothing is ever clipped off-screen. */
  max-height: 90vh;
  max-height: 90dvh;
  background: var(--duallo-card-gradient);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
  overflow: hidden;
  z-index: 1100;
  display: flex;
  flex-direction: column;
}

.overlay-header {
  cursor: move;
  padding: 12px 12px 12px 18px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  font-size: 0.98rem;
  color: #eef3f2;
  flex: 0 0 auto;
}

@media (hover: none) {
  .overlay-header {
    cursor: default;
  }
}

.overlay-content {
  padding: 18px;
  flex: 1 1 auto;
  min-height: 0;
  text-align: left;
}

.close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: none;
  border: none;
  color: #b7c5cc;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
</style>